/**
 * Pedirle al navegador que conserve la base local.
 *
 * Sin almacenamiento persistente, el navegador puede desalojar IndexedDB cuando
 * el disco escasea, y ahí viven las ventas que todavía no han subido a Business
 * Central. La API existía en el proyecto --`requestPersistentStorage`, en
 * `components/Storage/StorageUtils.js`-- pero no la llamaba nadie: sólo se podía
 * conceder a mano desde la pantalla de almacenamiento.
 *
 * Se pide **al arrancar** y una sola vez por carga. En Chrome no abre ningún
 * diálogo: se concede o se niega según el uso del sitio --instalado como
 * aplicación, marcado, visitado a menudo--, así que para el cajero es invisible.
 *
 * **No lanza nunca.** Un navegador sin la API, o que niegue el permiso, no puede
 * impedir que la caja arranque: esto es una protección, no un requisito.
 */
import { isStoragePersistent, requestPersistentStorage } from "../components/Storage/StorageUtils";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("almacenamiento");

/**
 * @returns {Promise<{persistente: boolean, soportado: boolean, yaLoEra: boolean}>}
 */
export const pedirAlmacenamientoPersistente = async () => {
  const soportado = Boolean(
    typeof navigator !== "undefined" && navigator.storage && navigator.storage.persist
  );
  if (!soportado) {
    registro.warn(
      "Este navegador no permite pedir almacenamiento persistente: la base local puede desalojarse si falta espacio."
    );
    return { persistente: false, soportado: false, yaLoEra: false };
  }

  try {
    const yaLoEra = await isStoragePersistent();
    if (yaLoEra) return { persistente: true, soportado: true, yaLoEra: true };

    const concedido = await requestPersistentStorage();
    if (concedido) registro.log("El navegador concedió almacenamiento persistente.");
    else
      registro.warn(
        "El navegador no concedió almacenamiento persistente: puede desalojar la base local, con las ventas que no han subido dentro."
      );

    return { persistente: Boolean(concedido), soportado: true, yaLoEra: false };
  } catch (error) {
    // El respaldo es no saberlo, no romper el arranque.
    registro.error("No se pudo pedir almacenamiento persistente:", error);
    return { persistente: false, soportado: true, yaLoEra: false };
  }
};

export default pedirAlmacenamientoPersistente;
